import React from 'react';

interface CompanyStripeSettingsProps {
  initialData: any;
  loadingStripeSettings: boolean;
  stripeSettings: {
    stripe_enabled: boolean;
    stripe_active_environment: 'test' | 'production';
    has_stripe_test_secret_key: boolean;
    has_stripe_production_secret_key: boolean;
    stripe_test_secret_key_display?: string;
    stripe_production_secret_key_display?: string;
  };
  stripeFeedback: string;
  stripeValidationFeedback: string;
  validatingStripeSettings: boolean;
  savingStripeSettings: boolean;
  handleStripeChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  handleValidateStripeSettings: () => void;
  handleSaveStripeSettings: () => void;
  openSecretKeyModal: (env: 'test' | 'production') => void;
}

export const CompanyStripeSettings: React.FC<CompanyStripeSettingsProps> = ({
  initialData,
  loadingStripeSettings,
  stripeSettings,
  stripeFeedback,
  stripeValidationFeedback,
  validatingStripeSettings,
  savingStripeSettings,
  handleStripeChange,
  handleValidateStripeSettings,
  handleSaveStripeSettings,
  openSecretKeyModal,
}) => {
  const activeEnv = stripeSettings.stripe_active_environment;
  const isTest = activeEnv === 'test';

  return (
    <div className="form-section">
      <h3>Stripe Payment Settings</h3>
      {!initialData?.id ? (
        <p style={{ color: 'var(--color-neutral-graphite)' }}>
          Save the company first, then configure Stripe credentials.
        </p>
      ) : (
        <>
          {loadingStripeSettings ? (
            <p style={{ color: 'var(--color-neutral-graphite)' }}>Loading Stripe settings...</p>
          ) : (
            <>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="stripe_enabled"
                    checked={stripeSettings.stripe_enabled}
                    onChange={handleStripeChange}
                  />
                  Enable Stripe payments
                </label>
              </div>

              {/* Environment Selector */}
              <div className="form-group">
                <label htmlFor="stripe_active_environment">Environment</label>
                <select
                  id="stripe_active_environment"
                  name="stripe_active_environment"
                  value={stripeSettings.stripe_active_environment}
                  onChange={handleStripeChange}
                  className="form-control"
                  style={{ maxWidth: '200px' }}
                >
                  <option value="test">Test</option>
                  <option value="production">Production</option>
                </select>
                <small className="form-text text-muted">
                  Select which Stripe environment to use for payments
                </small>
              </div>

              {/* Restricted API Key Field with Censored Display */}
              <div className="form-group">
                <label htmlFor={`stripe_${activeEnv}_secret_key_display`}>
                  Restricted API Key {isTest ? '(Test)' : '(Production)'}
                  {isTest 
                    ? stripeSettings.has_stripe_test_secret_key 
                      ? <span className="badge badge-success" style={{ marginLeft: '8px' }}>Stored</span>
                      : <span className="badge badge-secondary" style={{ marginLeft: '8px' }}>Not Set</span>
                    : stripeSettings.has_stripe_production_secret_key 
                      ? <span className="badge badge-success" style={{ marginLeft: '8px' }}>Stored</span>
                      : <span className="badge badge-secondary" style={{ marginLeft: '8px' }}>Not Set</span>
                  }
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="password"
                    id={`stripe_${activeEnv}_secret_key_display`}
                    name={`stripe_${activeEnv}_secret_key_display`}
                    value={isTest ? stripeSettings.stripe_test_secret_key_display || '' : stripeSettings.stripe_production_secret_key_display || ''}
                    onChange={handleStripeChange}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openSecretKeyModal(activeEnv);
                    }}
                    className="form-control"
                    placeholder={isTest ? 'rk_test_...' : 'rk_live_...'}
                    readOnly
                    style={{ fontFamily: 'monospace', cursor: 'pointer' }}
                    title="Click to update key"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openSecretKeyModal(activeEnv);
                    }}
                    title="Update key"
                  >
                    <span style={{ fontSize: '0.9em' }}>✏️</span>
                  </button>
                </div>
                <small className="form-text text-muted">
                  Restricted API key (rk_*). Click the edit button to update. 
                  Never expose this key in client-side code.
                </small>
              </div>

              {/* Action Buttons */}
              <div className="form-actions" style={{ marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleValidateStripeSettings}
                  disabled={validatingStripeSettings}
                >
                  {validatingStripeSettings ? 'Validating...' : 'Test Connection'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveStripeSettings}
                  disabled={savingStripeSettings}
                >
                  {savingStripeSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>

              {/* Feedback Messages */}
              {stripeFeedback && (
                <p style={{ marginTop: '10px', color: 'var(--color-neutral-graphite)' }}>{stripeFeedback}</p>
              )}
              {stripeValidationFeedback && (
                <p style={{ 
                  marginTop: '6px', 
                  color: stripeValidationFeedback.includes('valid') || stripeValidationFeedback.includes('success') 
                    ? 'var(--color-success)' 
                    : 'var(--color-danger)' 
                }}>
                  {stripeValidationFeedback}
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};